import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../../firestore"
import { CreatedAt, UpdatedAt } from "../system"
import { SubscriptionHistoryId, SubscriptionId, SubscriptionIdSchema, UserId, UserIdSchema } from "../../id"
import { SubscriptionKindNormal, SubscriptionKindPremium, SubscriptionKindPremiumPlus } from "./subscription-kind"
import { PlatformStore } from "../platform"
import { SubscriptionPlatform } from "./subscription"

export const SubscriptionHistoryEventType = z.union([
  z.literal("subscribe"),
  z.literal("renew"),
  z.literal("upgrade"),
  z.literal("downgrade"),
  z.literal("cancel"),
  z.literal("payment_failed"),
  z.literal("expire"),
  z.literal("refund"),
]).describe("イベント種別。subscribe: 新規契約、renew: 自動更新、upgrade: アップグレード、downgrade: ダウングレード、cancel: 解約、payment_failed: 課金失敗、expire: 期限切れ、refund: 返金")

export type SubscriptionHistoryEventType = z.infer<typeof SubscriptionHistoryEventType>

export const SubscriptionHistoryKind = z.union([
  SubscriptionKindNormal,
  SubscriptionKindPremium,
  SubscriptionKindPremiumPlus,
])

export type SubscriptionHistoryKind = z.infer<typeof SubscriptionHistoryKind>

export const SubscriptionHistoryDocumentMeta = z.object({
  user_id: UserIdSchema,
  subscription_id: SubscriptionIdSchema.optional().describe("サブスクリプション契約のID"),
  event_type: SubscriptionHistoryEventType,
  kind: SubscriptionHistoryKind.describe("記録時のプラン"),
  expire_at: z.number().optional().describe("有効期限（Unix timestamp、有料プランの場合）"),
  platform: SubscriptionPlatform.optional().describe("課金プラットフォーム（iap: App Store, iab: Google Play, admin: 管理者付与）"),
  metadata: z.record(z.string(), z.any()).optional().describe("追加情報"),
})

export type SubscriptionHistoryDocumentMeta = z.infer<typeof SubscriptionHistoryDocumentMeta>

export const SubscriptionHistoryDocumentBody = z.intersection(
  SubscriptionHistoryDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SubscriptionHistoryDocumentBody = z.infer<typeof SubscriptionHistoryDocumentBody>

export type SubscriptionHistoryDocument = SubscriptionHistoryDocumentBody & {
  id: SubscriptionHistoryId,
}

export class SubscriptionHistoryDocuments {

  static toDocumentData(doc: SubscriptionHistoryDocumentBody): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SubscriptionHistoryDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    // 新形式（platform: "iap" | "iab" | "admin"）と旧形式（store: 0 | 1）の両方に対応
    let platform: SubscriptionPlatform | undefined
    if (data["platform"] !== undefined) {
      // 新形式: platform フィールドがある場合はそのまま使用
      platform = data["platform"]
    } else if (data["store"] !== undefined) {
      // 旧形式: store フィールド（数値）を platform（文字列）に変換
      platform = data["store"] === PlatformStore.IAP ? "iap" : "iab"
    }

    return {
      id: SubscriptionHistoryId(doc.id),
      user_id: UserId(data["user_id"]),
      subscription_id: data["subscription_id"] ? SubscriptionId(data["subscription_id"]) : undefined,
      event_type: data["event_type"],
      kind: data["kind"],
      expire_at: data["expire_at"],
      platform,
      metadata: data["metadata"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
